import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { WidaStudent, WidaYearRecord } from '../Upload/widaHistory';
import { PlanCard } from './components';
import { LineChart, ResetSplitChart } from './charts';
import { COLORS, INFO_BOX } from './tokens';
import { detectResetYear, domainSeries, splitSeriesByReset } from './derive';
import { useReclassCriteria } from './reclassCriteriaContext';

function shortDate(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
}

function gradeLabel(grade: string): string {
  const n = grade.replace(/^0+/, '');
  return n ? `Grade ${n}` : 'an earlier grade';
}

const PACE_TOLERANCE = 0.1;

function movementVerb(net: number): string {
  if (net > 0) return 'grew';
  if (net < 0) return 'declined';
  return 'were unchanged';
}

function paceClause(levels: number[]): string {
  if (levels.length < 3) return '';
  const deltas = levels.slice(1).map((level, i) => level - (levels[i] as number));
  const latest = deltas[deltas.length - 1] as number;
  const earlier = deltas.slice(0, -1);
  const earlierPace = earlier.reduce((sum, d) => sum + d, 0) / earlier.length;
  if (latest < earlierPace - PACE_TOLERANCE) return ', with growth slowing in recent years';
  if (latest > earlierPace + PACE_TOLERANCE) return ', with growth accelerating in recent years';
  return ', at a steady pace';
}

function growthSummary(student: WidaStudent, resetYear: number | null): string {
  const scored = student.years.filter(
    (y): y is WidaYearRecord => !y.waived && y.composite.level != null,
  );
  if (scored.length < 2) return '';
  const firstName = student.firstName || 'This student';
  const levelOf = (y: WidaYearRecord) => y.composite.level as number;
  if (resetYear != null) {
    const preReset = scored.filter((y) => y.year < resetYear);
    const first = preReset[0];
    const last = preReset[preReset.length - 1];
    if (first && last && first !== last) {
      const verb = movementVerb(levelOf(last) - levelOf(first));
      const pace = paceClause(preReset.map(levelOf));
      return `Pre-reset composite levels ${verb} from ${levelOf(first).toFixed(2)} (${gradeLabel(first.grade)}) to ${levelOf(last).toFixed(2)} (${gradeLabel(last.grade)})${pace}; the ${resetYear} administration is ${firstName}'s first post-reset score on file.`;
    }
  }
  const first = scored[0];
  const last = scored[scored.length - 1];
  if (!first || !last) return '';
  const verb = movementVerb(levelOf(last) - levelOf(first));
  return `Composite proficiency ${verb} from ${levelOf(first).toFixed(2)} (${gradeLabel(first.grade)}) to ${levelOf(last).toFixed(2)} (${gradeLabel(last.grade)}) across ${scored.length} scored administrations.`;
}

export function CompositeTrajectory({ student }: { student: WidaStudent }) {
  const criteria = useReclassCriteria();
  const points = domainSeries(student, 'composite').map((p) => ({ year: p.year, level: p.level }));
  if (points.length < 2) return null;

  const waivedYear = student.years.find((y) => y.waived)?.year;
  const resetYear = detectResetYear(student);
  const { pre, post } = splitSeriesByReset(points, resetYear);
  const splitMode = resetYear != null && pre.length > 0 && post.length > 0;
  const summary = growthSummary(student, resetYear);
  const threshold =
    criteria.compositeMin != null
      ? { value: criteria.compositeMin, label: `Exit · ${criteria.compositeMin}` }
      : undefined;

  return (
    <PlanCard
      title="Composite Trajectory"
      subtitle="Composite proficiency level (1–6) across scored WIDA ACCESS administrations."
    >
      {!splitMode && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 28, height: 2, borderRadius: 999, bgcolor: COLORS.navy }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.ink }}>Historical Pace</Typography>
        </Box>
      )}

      <Box
        sx={{
          bgcolor: COLORS.bg,
          borderRadius: 2,
          p: 2.5,
          boxShadow: 'inset 0 0 0 1px rgba(8,8,27,0.04)',
        }}
      >
        {splitMode && resetYear != null ? (
          <ResetSplitChart
            prePoints={pre}
            postPoints={post}
            resetYear={resetYear}
            height={320}
            yMin={1}
            yMax={6}
            yTicks={[1, 2, 3, 4, 5, 6]}
            threshold={threshold}
            gapYear={waivedYear}
          />
        ) : (
          <LineChart
            points={points}
            height={320}
            yMin={1}
            yMax={6}
            yTicks={[1, 2, 3, 4, 5, 6]}
            threshold={threshold}
            gapYear={waivedYear}
          />
        )}
      </Box>

      {summary && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: INFO_BOX.bg,
            border: `1px solid ${INFO_BOX.border}`,
            borderRadius: 2,
            px: 2,
            py: 1.75,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 22, color: INFO_BOX.accent, flexShrink: 0, mt: '1px' }} />
          <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.55, color: COLORS.ink }}>
            {summary}
          </Typography>
        </Box>
      )}

      {resetYear != null && (
        <Typography sx={{ fontSize: 12, fontStyle: 'italic', fontWeight: 500, color: COLORS.muted, lineHeight: 1.5 }}>
          Historical view of composite scores. The {resetYear - 1}–{resetYear} reset means pre-reset and post-reset
          scale scores aren&rsquo;t directly comparable; proficiency levels (shown here) remain the comparable measure,
          and multi-year projections resume after a second post-reset ACCESS administration. Updated {shortDate(new Date())}.
        </Typography>
      )}
    </PlanCard>
  );
}
